import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, Layers } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { trackCTA } from '../../../utils/metaPixel';

import AgenticCareerWorkspacePreview from './AgenticCareerWorkspacePreview';
import AgenticJobSearchFeaturePreview from './AgenticJobSearchFeaturePreview';
import ATSScoreEnginePreview from './ATSScoreEnginePreview';
import AutonomousMatchFeedPreview from './AutonomousMatchFeedPreview';
import { HeroPreviewCopyProvider } from './useHeroPreviewCopy';

/** Default dwell; ATS script needs ~22s to reach resume canvas */
const DEFAULT_DURATION_MS = 15000;

interface HomeFeaturesSectionProps {
  onNavigate?: (view: 'home' | 'login' | 'signup' | 'contact-us' | 'optimize') => void;
}

type FeatureId = 'agentic-hunt' | 'resume-optimize' | 'job-recs' | 'ats-generate';
type PreviewKind = 'agent-search' | 'ats-score' | 'match-feed' | 'agent-workspace';

const FEATURES: ReadonlyArray<{ id: FeatureId; preview: PreviewKind; durationMs: number }> = [
  { id: 'agentic-hunt', preview: 'agent-search', durationMs: 16000 },
  { id: 'resume-optimize', preview: 'ats-score', durationMs: 26000 },
  { id: 'job-recs', preview: 'match-feed', durationMs: 19000 },
  { id: 'ats-generate', preview: 'agent-workspace', durationMs: 24000 },
];

function FeaturePreviewPanel({ feature }: { feature: (typeof FEATURES)[number] }) {
  const panel = (() => {
    if (feature.preview === 'agent-search') return <AgenticJobSearchFeaturePreview />;
    if (feature.preview === 'ats-score') return <ATSScoreEnginePreview />;
    if (feature.preview === 'match-feed') return <AutonomousMatchFeedPreview />;
    return <AgenticCareerWorkspacePreview />;
  })();
  return <HeroPreviewCopyProvider>{panel}</HeroPreviewCopyProvider>;
}

const HomeFeaturesSection: React.FC<HomeFeaturesSectionProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const timerRef = useRef<number | null>(null);

  const scheduleAdvance = useCallback((index: number) => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    const duration = FEATURES[index]?.durationMs ?? DEFAULT_DURATION_MS;
    timerRef.current = window.setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % FEATURES.length);
    }, duration);
  }, []);

  useEffect(() => {
    scheduleAdvance(activeIndex);
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, [activeIndex, scheduleAdvance]);

  const handleSelect = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  const activeFeature = FEATURES[activeIndex];

  return (
    <section id="features-overview" className="hfs scroll-mt-28">
      <div className="hfs__grid">
        <div className="hfs__left">
          <div className="hfs__header">
            <p className="hfs__eyebrow">
              <Layers className="h-4 w-4 shrink-0" aria-hidden />
              {t('home.features.eyebrow')}
            </p>
            <h2 className="hfs__title">{t('home.features.title')}</h2>
            <p className="hfs__desc">{t('home.features.description')}</p>
            <button
              type="button"
              className="hfs__cta"
              onClick={() => {
                trackCTA('home_features_get_started');
                onNavigate?.('signup');
              }}
            >
              {t('home.features.cta')}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </button>
          </div>

          <div className="hfs__nav">
            <div className="hfs__dashes" aria-hidden>
              {FEATURES.map((feat, i) => (
                <span
                  key={feat.id}
                  className={i === activeIndex ? 'hfs__dash hfs__dash--active' : 'hfs__dash'}
                />
              ))}
            </div>

            <div className="hfs__list" role="tablist" aria-label={t('home.features.eyebrow')}>
              {FEATURES.map((feat, i) => {
                const isActive = activeIndex === i;
                return (
                  <button
                    key={feat.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`hfs__card${isActive ? ' hfs__card--active' : ''}`}
                    onClick={() => handleSelect(i)}
                  >
                    <div className="hfs__card-body">
                      <span className="hfs__card-title">
                        {t(`home.features.items.${feat.id}.title`)}
                      </span>
                      <AnimatePresence initial={false}>
                        {isActive && (
                          <motion.p
                            key={`${feat.id}-desc`}
                            initial={{ opacity: 0, height: 0, marginTop: 0 }}
                            animate={{ opacity: 1, height: 'auto', marginTop: '0.5rem' }}
                            exit={{ opacity: 0, height: 0, marginTop: 0 }}
                            transition={{ duration: 0.28, ease: 'easeOut' }}
                            className="hfs__card-desc"
                          >
                            {t(`home.features.items.${feat.id}.description`)}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Desktop: full interactive preview | Mobile: hidden to cut scroll fatigue */}
        <div className="hfs__visual-wrap hidden md:block">
          <div className="hfs__visual">
            <div className="hfs__mesh" aria-hidden />

            <div className="hfs__preview-context">
              <AnimatePresence>
                <motion.div
                  key={activeFeature.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18, ease: 'easeInOut' }}
                  className="hfs__frame"
                >
                  <FeaturePreviewPanel feature={activeFeature} />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeFeaturesSection;
